<template>
  <div class="security-logs-viewer">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4>📋 Logs de Segurança</h4>
      <div class="d-flex gap-2">
        <select
          v-model="filterHours"
          @change="loadLogs"
          class="form-control form-control-sm"
          style="width: auto"
        >
          <option value="1">Última hora</option>
          <option value="24">Últimas 24 horas</option>
          <option value="168">Última semana</option>
          <option value="720">Último mês</option>
        </select>
        <button @click="loadLogs" class="btn btn-sm btn-primary">
          <i class="fas fa-sync-alt"></i> Atualizar
        </button>
        <button
          @click="exportLogs"
          class="btn btn-sm btn-success"
          v-if="logs.length > 0"
        >
          <i class="fas fa-download"></i> Exportar
        </button>
      </div>
    </div>

    <!-- Estatísticas resumidas -->
    <div class="row mb-4">
      <div class="col-md-3">
        <div class="stat-card text-center">
          <h6 class="text-muted">Total de Eventos</h6>
          <h3 class="text-primary">{{ stats.total_events || 0 }}</h3>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card text-center">
          <h6 class="text-muted">DevTools Aberto</h6>
          <h3 class="text-warning">{{ stats.devtools_opened || 0 }}</h3>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card text-center">
          <h6 class="text-muted">Usuários Únicos</h6>
          <h3 class="text-info">{{ stats.unique_users || 0 }}</h3>
        </div>
      </div>
      <div class="col-md-3">
        <div class="stat-card text-center">
          <h6 class="text-muted">Período</h6>
          <h3 class="text-secondary">{{ filterHours }}h</h3>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-4">
      <i class="fas fa-spinner fa-spin fa-2x text-primary"></i>
      <p class="mt-2">Carregando logs...</p>
    </div>

    <!-- Lista de logs -->
    <div v-else-if="logs.length > 0" class="logs-container">
      <div class="table-responsive">
        <table class="table table-striped table-hover">
          <thead class="thead-dark">
            <tr>
              <th width="150">Data/Hora</th>
              <th width="120">Usuário</th>
              <th width="150">Evento</th>
              <th width="200">Página</th>
              <th width="100">IP</th>
              <th>Detalhes</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="log in paginatedLogs"
              :key="log.id"
              :class="getRowClass(log.event_type)"
            >
              <td>
                <small>{{ formatDateTime(log.created_at) }}</small>
              </td>
              <td>
                <span class="badge badge-secondary" v-if="log.user">
                  {{ log.user.name }}
                </span>
                <span class="text-muted" v-else>Anônimo</span>
              </td>
              <td>
                <span class="badge" :class="getEventBadgeClass(log.event_type)">
                  {{ getEventLabel(log.event_type) }}
                </span>
              </td>
              <td>
                <small
                  class="text-truncate d-block"
                  style="max-width: 200px"
                  :title="log.page_url"
                >
                  {{ getPageName(log.page_url) }}
                </small>
              </td>
              <td>
                <small class="font-monospace">{{ log.ip_address }}</small>
              </td>
              <td>
                <button
                  @click="showDetails(log)"
                  class="btn btn-sm btn-outline-info"
                >
                  <i class="fas fa-eye"></i> Ver
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginação simples -->
      <div class="d-flex justify-content-between align-items-center mt-3">
        <div>
          <small class="text-muted">
            Mostrando {{ (currentPage - 1) * perPage + 1 }} a
            {{ Math.min(currentPage * perPage, logs.length) }} de
            {{ logs.length }} registros
          </small>
        </div>
        <div>
          <button
            @click="currentPage--"
            :disabled="currentPage === 1"
            class="btn btn-sm btn-outline-secondary me-2"
          >
            Anterior
          </button>
          <span class="mx-3">Página {{ currentPage }} de {{ totalPages }}</span>
          <button
            @click="currentPage++"
            :disabled="currentPage === totalPages"
            class="btn btn-sm btn-outline-secondary"
          >
            Próxima
          </button>
        </div>
      </div>
    </div>

    <!-- Sem dados -->
    <div v-else class="text-center py-5">
      <i class="fas fa-shield-alt fa-3x text-muted mb-3"></i>
      <h5 class="text-muted">Nenhum evento de segurança registrado</h5>
      <p class="text-muted">
        Os logs aparecem aqui quando alguém abre as DevTools
      </p>
    </div>

    <!-- Modal de detalhes -->
    <div class="modal fade" id="detailsModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Detalhes do Log</h5>
            <button type="button" class="close" data-dismiss="modal">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body" v-if="selectedLog">
            <div class="row">
              <div class="col-md-6">
                <strong>ID:</strong> {{ selectedLog.id }}<br />
                <strong>Data:</strong>
                {{ formatDateTime(selectedLog.created_at) }}<br />
                <strong>Usuário:</strong>
                {{ selectedLog.user ? selectedLog.user.name : "Anônimo" }}<br />
                <strong>Evento:</strong>
                {{ getEventLabel(selectedLog.event_type) }}<br />
                <strong>IP:</strong> {{ selectedLog.ip_address }}<br />
              </div>
              <div class="col-md-6">
                <strong>URL:</strong><br />
                <small class="text-break">{{ selectedLog.page_url }}</small
                ><br /><br />
                <strong>User Agent:</strong><br />
                <small class="text-break">{{ selectedLog.user_agent }}</small>
              </div>
            </div>

            <div class="mt-3" v-if="selectedLog.additional_data">
              <strong>Dados Adicionais:</strong>
              <pre class="bg-light p-2 mt-2 small">{{
                JSON.stringify(selectedLog.additional_data, null, 2)
              }}</pre>
            </div>

            <div class="mt-3" v-if="selectedLog.description">
              <strong>Descrição:</strong>
              <p class="mt-1">{{ selectedLog.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "SecurityLogsViewer",
  data() {
    return {
      logs: [],
      stats: {},
      loading: false,
      filterHours: 24,
      selectedLog: null,
      currentPage: 1,
      perPage: 20,
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.logs.length / this.perPage);
    },

    paginatedLogs() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.logs.slice(start, end);
    },
  },

  methods: {
    async loadLogs() {
      this.loading = true;
      try {
        // Carregar logs
        const logsResponse = await axios.get(
          `/api/v1/security-logs?hours=${this.filterHours}&per_page=1000`
        );
        this.logs = logsResponse.data.data.data || [];

        // Carregar estatísticas
        const statsResponse = await axios.get(
          `/api/v1/security-stats?hours=${this.filterHours}`
        );
        this.stats = statsResponse.data.data || {};

        this.currentPage = 1; // Resetar paginação
      } catch (error) {
        console.error("Erro ao carregar logs:", error);
        this.$toast?.error("Erro ao carregar logs de segurança");
      } finally {
        this.loading = false;
      }
    },

    showDetails(log) {
      this.selectedLog = log;
      $("#detailsModal").modal("show");
    },

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString("pt-BR");
    },

    getEventLabel(eventType) {
      const labels = {
        devtools_opened: "DevTools Aberto",
        devtools_closed: "DevTools Fechado",
        security_violation: "Violação de Segurança",
        suspicious_activity: "Atividade Suspeita",
      };
      return labels[eventType] || eventType;
    },

    getEventBadgeClass(eventType) {
      const classes = {
        devtools_opened: "badge-warning",
        devtools_closed: "badge-success",
        security_violation: "badge-danger",
        suspicious_activity: "badge-warning",
      };
      return classes[eventType] || "badge-secondary";
    },

    getRowClass(eventType) {
      if (eventType === "devtools_opened") return "table-warning";
      if (eventType === "security_violation") return "table-danger";
      return "";
    },

    getPageName(url) {
      try {
        const urlObj = new URL(url);
        return urlObj.pathname;
      } catch {
        return url;
      }
    },

    exportLogs() {
      const csvContent = this.generateCSV();
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `security-logs-${new Date().toISOString().split("T")[0]}.csv`
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    generateCSV() {
      const headers = [
        "Data/Hora",
        "Usuário",
        "Evento",
        "Página",
        "IP",
        "Descrição",
      ];
      const rows = this.logs.map((log) => [
        this.formatDateTime(log.created_at),
        log.user ? log.user.name : "Anônimo",
        this.getEventLabel(log.event_type),
        log.page_url,
        log.ip_address,
        log.description || "",
      ]);

      const csvContent = [headers, ...rows]
        .map((row) => row.map((cell) => `"${cell}"`).join(","))
        .join("\n");

      return csvContent;
    },
  },

  mounted() {
    this.loadLogs();

    // Auto-refresh a cada 30 segundos
    this.refreshInterval = setInterval(() => {
      this.loadLogs();
    }, 30000);
  },

  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
};
</script>

<style scoped>
.security-logs-viewer {
  padding: 20px;
}

.stat-card {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 10px;
}

.logs-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table {
  margin-bottom: 0;
}

.table th {
  border-top: none;
  font-size: 14px;
  font-weight: 600;
}

.table td {
  vertical-align: middle;
  font-size: 13px;
}

.font-monospace {
  font-family: "Courier New", monospace;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

pre {
  font-size: 11px;
  max-height: 200px;
  overflow-y: auto;
}

.modal-lg {
  max-width: 800px;
}

.gap-2 > * {
  margin-right: 8px;
}

.gap-2 > *:last-child {
  margin-right: 0;
}

.me-2 {
  margin-right: 0.5rem;
}
</style>
